// features/GroundTruth/groundTruthApi.js
//
// The ground truth endpoints, as plain functions for React Query, in the
// same shape as the other analyses' beside them. A photo goes up on its own,
// as soon as it is chosen, through XMLHttpRequest rather than fetch, because
// only XHR reports how far an upload has got.

import Cookies from "js-cookie";
import { CSRF_COOKIE_NAME, SARFINDER_BE_API_URL } from "utils/constants";

const BASE = `${SARFINDER_BE_API_URL}/groundtruth`;

/** DRF's {field: [messages]} as one line, for a snackbar or a form. */
export const flattenError = (payload, status) => {
  const flatten = (value) =>
    Array.isArray(value)
      ? value.map(flatten).filter(Boolean).join(", ")
      : value && typeof value === "object"
        ? Object.values(value).map(flatten).filter(Boolean).join(", ")
        : value
          ? String(value)
          : "";
  const detail =
    payload?.error ||
    payload?.detail ||
    Object.values(payload || {})
      .map(flatten)
      .filter(Boolean)
      .join("; ");
  return detail || `Request failed (${status})`;
};

const request = async (path, { method = "GET", body } = {}) => {
  const response = await fetch(`${BASE}${path}`, {
    method,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": Cookies.get(CSRF_COOKIE_NAME),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (response.status === 204) return null;
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    // An import's refusal lists the features it left out; the message is
    // what it says about them, not the list.
    const { skipped, ...said } = payload || {};
    const error = new Error(flattenError(said, response.status));
    error.status = response.status;
    error.skipped = Array.isArray(skipped) ? skipped : null;
    error.noneUsable = response.status === 400 && !!error.skipped;
    throw error;
  }
  return payload;
};

/* ── Collections: the folders labels are kept in ─────────────────────── */

export const listCollections = (eventName) =>
  request(`/collections/?event_name=${encodeURIComponent(eventName)}`);

/** `{eventName, name, parent?, shared?}`: a new collection, one's own. */
export const createCollection = ({ eventName, name, parent, shared }) =>
  request("/collections/", {
    method: "POST",
    body: {
      event_name: eventName,
      name,
      ...(parent ? { parent } : {}),
      ...(shared !== undefined ? { shared } : {}),
    },
  });

/** `{name?, shared?, parent?}`: only what is given changes. */
export const updateCollection = (id, changes) =>
  request(`/collections/${id}/`, { method: "PATCH", body: changes });

/** The most labels one request of an import carries (the server's IMPORT_CHUNK is 2,500). */
export const IMPORT_PART_LABELS = 2000;
/**
 * About the most one request of an import carries, in bytes of JSON: well
 * inside Django's 2.5 MB and a proxy's usual 1 MB.
 */
export const IMPORT_PART_BYTES = 800_000;

/**
 * The labels split into the parts an import sends, in order: each at most
 * IMPORT_PART_LABELS long and, unless one label is larger on its own, about
 * IMPORT_PART_BYTES of JSON.
 */
export function importParts(
  labels,
  { maxLabels = IMPORT_PART_LABELS, maxBytes = IMPORT_PART_BYTES } = {},
) {
  const parts = [];
  let part = [];
  let bytes = 0;
  (labels || []).forEach((label) => {
    const size = JSON.stringify(label).length + 1;
    if (part.length && (part.length >= maxLabels || bytes + size > maxBytes)) {
      parts.push(part);
      part = [];
      bytes = 0;
    }
    part.push(label);
    bytes += size;
  });
  if (part.length) parts.push(part);
  return parts;
}

/**
 * An uploaded file's features as a new collection of one's own:
 * `labels` are {geometry, category, name?, description?, observed_on?}.
 * Resolves with {collection, created, skipped_count, skipped: [{index,
 * error}]}; features that can't be labels are left out, not refused.
 *
 * A file too large for one request goes in parts (importParts): the first
 * part the server can make labels of makes the collection, the rest are
 * added to it. `onProgress(sent, total)` follows them. If a later part
 * fails, the collection is deleted, so the file comes in whole or not at
 * all, and the error is thrown.
 */
export async function importCollection({
  eventName,
  name,
  labels,
  onProgress,
}) {
  const parts = importParts(labels);
  const total = labels?.length || 0;
  let collection = null;
  let created = 0;
  let skippedCount = 0;
  const skipped = [];
  let offset = 0;
  let firstRefusal = null;
  try {
    for (const part of parts) {
      let result;
      if (!collection) {
        try {
          result = await request("/collections/import/", {
            method: "POST",
            body: { event_name: eventName, name, labels: part, offset },
          });
        } catch (error) {
          // None of this part could be labels: the next may still make it.
          if (!error.noneUsable || part === parts[parts.length - 1])
            throw error;
          firstRefusal = firstRefusal || error;
          skippedCount += part.length;
          skipped.push(...(error.skipped || []));
          offset += part.length;
          onProgress?.(offset, total);
          continue;
        }
        collection = result.collection;
      } else {
        result = await request(`/collections/${collection.id}/import/`, {
          method: "POST",
          body: { labels: part, offset },
        });
        collection = result.collection;
      }
      created += result.created;
      skippedCount += result.skipped_count;
      skipped.push(...(result.skipped || []));
      offset += part.length;
      onProgress?.(offset, total);
    }
  } catch (error) {
    if (collection) {
      await request(`/collections/${collection.id}/`, {
        method: "DELETE",
      }).catch(() => {});
    }
    throw firstRefusal && !collection ? firstRefusal : error;
  }
  return {
    collection,
    created,
    skipped_count: skippedCount,
    skipped: skipped.slice(0, 50),
  };
}

/** Deletes it, the collections inside it, and every label in them. */
export const deleteCollection = (id) =>
  request(`/collections/${id}/`, { method: "DELETE" });

/* ── Labels ──────────────────────────────────────────────────────────── */

export const listLabels = (eventName) =>
  request(`/labels/?event_name=${encodeURIComponent(eventName)}`);

/**
 * A short digest of the event's labels as this person sees them, which
 * changes with any of them: the list is fetched again only when it does.
 */
export const labelsFingerprint = (eventName) =>
  request(
    `/labels/fingerprint/?event_name=${encodeURIComponent(eventName)}`,
  ).then((result) => result?.fingerprint || null);

/** A label's fields as the API takes them; only those given are sent. */
const labelBody = (fields) => {
  const body = {};
  if (fields.eventName !== undefined) body.event_name = fields.eventName;
  if (fields.geometry !== undefined) body.geometry = fields.geometry;
  if (fields.category !== undefined) body.category = fields.category;
  if (fields.name !== undefined) body.name = fields.name;
  if (fields.description !== undefined) body.description = fields.description;
  if (fields.observedOn !== undefined)
    body.observed_on = fields.observedOn || null;
  if (fields.photoIds?.length) body.photo_ids = fields.photoIds;
  if (fields.collection !== undefined) body.collection = fields.collection;
  if (fields.position !== undefined) body.position = fields.position;
  return body;
};

export const createLabel = (fields) =>
  request("/labels/", { method: "POST", body: labelBody(fields) });

export const updateLabel = (id, fields) =>
  request(`/labels/${id}/`, { method: "PATCH", body: labelBody(fields) });

export const deleteLabel = (id) =>
  request(`/labels/${id}/`, { method: "DELETE" });

/**
 * What the damage map says under the event's labels not read yet:
 * {available, reason, readings: {id: reading}, remaining, failed}. One call
 * reads for up to about 20 s; `remaining` are left for the next. The first
 * time on an event this can take a minute: it fetches the damage map.
 */
export const readLabels = (eventName) =>
  request("/labels/readings/", {
    method: "POST",
    body: { event_name: eventName },
  });

/**
 * The check by area over the areas the page shows: the server counts the
 * ground under them (and, with `crop`, the rest of the crop as no damage)
 * against the damage map. {available, reason, result}.
 */
export const checkByArea = ({
  eventName,
  hiddenCollections,
  hiddenLabels,
  allHidden,
  exclude,
  crop,
}) =>
  request("/labels/area-check/", {
    method: "POST",
    body: {
      event_name: eventName,
      hidden_collections: hiddenCollections || [],
      hidden_labels: hiddenLabels || [],
      all_hidden: !!allHidden,
      exclude: exclude || [],
      crop: crop || null,
    },
  });

export const deletePhoto = (id) =>
  request(`/photos/${id}/`, { method: "DELETE" });

/**
 * Upload a photo, for a label being made (`eventName`) or a saved one
 * (`labelId`). `onProgress(0..1)` follows the upload; `signal` aborts it.
 * Resolves with the photo as the server has it, including where and when it
 * was taken if the photo says.
 */
export function uploadPhoto({ file, eventName, labelId, onProgress, signal }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${BASE}/photos/`);
    xhr.withCredentials = true;
    xhr.setRequestHeader("X-CSRFToken", Cookies.get(CSRF_COOKIE_NAME) || "");
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded / event.total);
    };
    xhr.onload = () => {
      let payload = {};
      try {
        payload = JSON.parse(xhr.responseText || "{}");
      } catch (error) {
        payload = {};
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(payload);
      else if (xhr.status === 413)
        reject(new Error("That photo is too large for the server to take."));
      else reject(new Error(flattenError(payload, xhr.status)));
    };
    xhr.onerror = () =>
      reject(
        new Error(
          "The upload was cut off. Check the connection and try again.",
        ),
      );
    xhr.onabort = () => {
      const error = new Error("Upload cancelled");
      error.name = "AbortError";
      reject(error);
    };
    signal?.addEventListener("abort", () => xhr.abort(), { once: true });

    const form = new FormData();
    form.append("file", file, file.name);
    if (labelId) form.append("label", labelId);
    else form.append("event_name", eventName);
    xhr.send(form);
  });
}
